<template>
  <div>
    <v-simple-table dense>
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">
              Activity
            </th>
            <th class="text-left">
              From
            </th>
            <th class="text-left">
              To
            </th>
            <th class="text-left">
              License Status
            </th>
            <th class="text-left">
              Registration Status
            </th>
            <th class="text-left">
              Effective State
            </th>
            <th class="text-right">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in activities"
            :key="item._id"
          >
            <td>
              <span :class="{ 'font-weight-bold': registration.primaryActivityId === item._id }">
                {{ _.get(item, 'memberType.name', item.type) }}
              </span>
              <span v-if="item.subType">
                - {{ _.get(subTypeMap, [item.type, item.subType, 'name'], item.subType) }}
              </span>
            </td>
            <td>{{ item.from | formatDate }}</td>
            <td>{{ item.to | formatDate }}</td>
            <td>{{ capitalCase(item.licenseStatus || '') }}</td>
            <td>{{ capitalCase(item.registrationStatus || '') }}</td>
            <td><v-icon>{{ item.effectiveState | checkMark }}</v-icon></td>
            <td class="text-right">
              <v-btn
                v-show="item.registrationStatus !== RegistrationStatus.COMPLETED"
                small
                text
                color="primary"
                @click="finaliseActivity(item)"
                :loading="finalising === item._id"
                :disabled="!!finalising"
                class="align-start"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>done</v-icon>
                Finalise
              </v-btn>
              <v-btn
                small
                text
                @click="editActivity(item)"
                class="align-start"
                :disabled="!!item.end"
              >
                <v-icon>edit</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <v-row>
      <v-col>
        <v-btn
          depressed
          tile
          class="ma-0"
          @click="addActivity"
        >
          <v-icon>
            add
          </v-icon>
          Add activity
        </v-btn>
      </v-col>
    </v-row>

    <v-dialog
      v-model="activityDialog"
      width="500"
    >
      <v-card>
        <v-card-text>
          <v-row class="pt-5">
            <v-col>
              <activity-form v-model="activityForm" />
              <registration-activity-details-form
                v-if="activityForm.type"
                v-model="activityDetailsForm"
                :memberType="activityForm.type"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn
            color="primary"
            text
            @click="save"
          >
            Save
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="activityDialog = false"
          >
            Cancel
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { RegistrationStatus } from '@mymsa/common'
import ActivityForm from './forms/ActivityForm.vue'
import RegistrationActivityDetailsForm from './forms/RegistrationActivityDetailsForm.vue'
import { capitalCase } from 'change-case'
import { cloneDeep, pick } from 'lodash'

export default {
  name: 'RegistrationActivitiesTable',

  components: { ActivityForm, RegistrationActivityDetailsForm },

  props: {
    registrationId: {
      type: String,
      required: true
    }
  },

  data: () => ({
    RegistrationStatus,
    registration: {
      activities: []
    },
    memberTypes: [],
    activities: [],
    activityDialog: false,
    activityForm: {},
    activityDetailsForm: {},
    finalising: null
  }),

  apollo: {
    registration: {
      query: gql`query GetRegistrationActivities($id: MongoID!) {
        registration: registrationById(_id: $id) {
          _id
          primaryActivityId
          activities {
            _id
            type
            subType
            from
            to
            licenseStatus
            registrationStatus
            siteVisitComplete
            siteVisitCompletedAt
            trainingComplete
            trainingCompletedAt
            effectiveState
            memberType {
              _id
              name
            }
            details {
              _id
              ausMeatEstablishmentNumber
              stateEstablishmentNumber
              nlisEstablishmentNumber
              nsqaAccredited
              nlisUserId
              gs1CompanyPrefix
              gs1GlobalLocationNumber
              nfasId
              agentName
              agentNumber
              stockTypeBeef
              stockTypeSheep
              propertyIdentificationCode
              lpaUserId
              chains {
                _id
                species
                chainNumber
                chainName
                immobiliser1Enabled
                immobiliser1Value
                immobiliser2Enabled
                immobiliser2Value
                immobiliser3Enabled
                immobiliser3Value
                stimulator1Enabled
                stimulator1Value
                stimulator2Enabled
                stimulator2Value
                stimulator3Enabled
                stimulator3Value
                hidePuller1Enabled
                hidePuller1Value
                hidePuller2Enabled
                hidePuller2Value
                hidePuller3Enabled
                hidePuller3Value
              }
            }
          }
        }
      }`,
      variables () {
        return {
          id: this.registrationId
        }
      },
      result ({ data }) {
        if (!data) return
        const { registration } = data
        this.activities = registration.activities ?? []
      },
      error (e) { this.onError(e) }
    },

    memberTypes: {
      query: gql`query {
        memberTypes: memberTypeMany(limit: 0) {
          _id
          value
          subTypes {
            _id
            value
            name
          }
        }
      }`,
      error (e) { this.onError(e) }
    }
  },

  computed: {
    subTypeMap () {
      return Object.fromEntries(this.memberTypes.map(memberType => [
        memberType.value,
        Object.fromEntries((memberType.subTypes ?? []).map(subType => [
          subType.value,
          subType
        ]))
      ]))
    }
  },

  methods: {
    capitalCase,

    onError (e) {
      this.$emit('error', e)
    },

    addActivity () {
      this.activityForm = {}
      this.activityDetailsForm = {
        chains: []
      }
      this.activityDialog = true
    },

    editActivity (activity) {
      this.activityForm = cloneDeep(pick(activity, [
        '_id',
        'type',
        'subType',
        'licenseStatus',
        'siteVisitComplete',
        'siteVisitCompletedAt',
        'trainingComplete',
        'trainingCompletedAt',
        'from',
        'to'
      ]))
      this.activityDetailsForm = cloneDeep(activity.details || {
        chains: []
      })
      this.activityDialog = true
    },

    async save () {
      const patch = {
        ...this.activityForm,
        details: this.activityDetailsForm
      }
      try {
        if (patch._id) await this.saveActivity(patch)
        else await this.saveNewActivity(patch)
      } catch (e) {
        this.onError(e)
        return
      }
      this.activityDialog = false
      this.$apollo.queries.registration.refresh()
      this.$emit('info', 'Activities saved.')
      this.$emit('change')
    },

    async saveNewActivity (activity) {
      await this.$apollo.mutate({
        mutation: gql`mutation ($id: MongoID!, $record: RegistrationActivitiesInput!) {
          registrationPushActivity(_id: $id, record: $record) { _id }
        }`,
        variables: {
          id: this.registrationId,
          record: {
            ...activity,
            registrationStatus: RegistrationStatus.SUBMITTED
          }
        }
      })
    },

    async saveActivity (activity) {
      await this.$apollo.mutate({
        mutation: gql`mutation ($id: MongoID!, $activityId: MongoID!, $record: UpdateByIdRegistrationActivitiesInput!) {
          registrationUpdateActivityById(_id: $id, activityId: $activityId, record: $record) { _id }
        }`,
        variables: {
          id: this.registrationId,
          activityId: activity._id,
          record: activity
        }
      })
    },

    async finaliseActivity (activity) {
      this.finalising = activity._id
      try {
        await this.$apollo.mutate({
          mutation: gql`mutation ($id: MongoID!, $activityId: MongoID!) {
            registrationFinaliseActivity(_id: $id, activityId: $activityId)
          }`,
          variables: {
            id: this.registrationId,
            activityId: activity._id
          }
        })
      } catch (e) {
        this.onError(e)
        return
      } finally {
        this.finalising = null
      }
      this.$apollo.queries.registration.refresh()
      this.$emit('change')
    }
  }
}
</script>
